<script setup lang="ts">
import { useAuth } from '@/composables/auth'
import { usePlayer, usePlayerProfit } from '@/composables/game/player'
import { computed, ref, watch } from 'vue'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import BoostProfitModal from '@/components/modals/BoostProfitModal.vue'
import router from '@/router'
import { RouteName, routePath } from '@/routes'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import CoinIconGold from '@/components/icons/CoinIconGold.vue'
import { T } from '@tolgee/vue'
import ArrowRight from '@/components/icons/ArrowRight.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import ThePremium from '@/components/ThePremium.vue'
import webapp from '@/services/webapp'
import HeaderMenu from '@/components/HeaderMenu.vue'
import ClockIcon from '@/components/icons/ClockIcon.vue'
import RocketEmoji from '@/assets/images/gifs/rocket.gif'
import ExplodeEmojisAnimation from '@/components/ExplodeEmojisAnimation.vue'

const { profile } = useAuth()
const {
  player,
  levelProgressPercentage,
} = usePlayer()
const {
  hasDoubleProfit,
  formattedDoubleProfitCountdown, playerProfit,
} = usePlayerProfit()
const { addHapticFeedback } = useHapticFeedback()
const currentRouteName = computed(() => router.currentRoute.value.name)
const isMinePage = computed(() => {
  return currentRouteName.value === RouteName.MINE
})

const showBoostProfitModal = ref(false)
const showCircleLineLottie = ref(false)

watch(playerProfit, (playerProfit, oldValue) => {
  if (playerProfit > oldValue) {
    showCircleLineLottie.value = true
    setTimeout(() => showCircleLineLottie.value = false, 2000)
  }
})
</script>

<template>
  <div class="px-4 pt-3 w-full flex items-center justify-between z-[82]">
    <div
      class="w-full flex items-center gap-x-1.5 flex-1 min-w-0"
    >
      <div class="flex items-center gap-x-1">
        <div
          class="flex items-center gap-x-1.5"
          @click="router.push({name: RouteName.PROFILE})"
        >
          <PlayerAvatar
            class="size-7 min-w-[28px]"
            :image-src="profile.photoUrl"
            :first-name="profile.firstName"
          />
          <div class="truncate flex-1 text-lg leading-6 -tracking-[0.36px] font-bold">
            {{ profile.firstName }}
          </div>
        </div>
        <ThePremium
          v-if="webapp.isPremium"
        />
      </div>
    </div>
    <HeaderMenu />
  </div>
  <div
    class="px-4 mt-3 w-full flex items-center justify-between max-[390px]:gap-x-3 gap-x-8  min-w-0 sticky top-1.5 z-[82]"
    :class="isMinePage ? 'pb-[26px] -mb-8' : 'pb-3'"
  >
    <div
      class="flex w-full flex-col gap-y-1 min-w-0"
      @click="addHapticFeedback('light'); router.push(routePath[RouteName.LEADERBOARD])"
    >
      <div class="flex items-center gap-x-1 min-w-0 px-1">
        <div class="flex items-center text-light-white min-w-0 drop-shadow-[0_0_6px_rgba(0,0,0,88%)]">
          <span class="text-sm leading-[18px] -tracking-[0.28px] font-semibold truncate ">
            <T
              key-name="general.level"
              default-value="Level"
            />
            {{ ' ' }}
            {{ player.level }}
          </span>
          <ArrowRight class="min-w-[16px] size-4" />
        </div>
      </div>
      <div class="min-[390px]:min-w-[158px] w-full rounded-full bg-dark-top-layer h-4 overflow-hidden p-0.5">
        <div
          class="w-none h-full rounded-[30px] transition-width duration-500 ease-in-out bg-xmas-gradient"
          :style="{ width: levelProgressPercentage + '%' }"
        />
      </div>
    </div>
    <div class="flex items-center justify-end">
      <div class="flex-1 max-w-fit bg-dark-top-layer rounded-full px-6 py-1.5 flex items-center">
        <div class="flex flex-col">
          <div
            class="text-white/60 font-medium text-xs whitespace-nowrap"
          >
            <T
              key-name="generic.profit_per_hour"
              default-value="Trading per hour"
            />
          </div>
          <div class="flex items-center justify-center gap-x-2">
            <VTooltip
              placement="bottom"
              :distance="16"
              popper-class="profit-tooltip"
              :disabled="!hasDoubleProfit && playerProfit < 1000"
            >
              <span
                class="flex items-center relative bg-[linear-gradient(90deg,_#FFF246_0%,_#E5B80B_37%,_#FFF571_68.5%,_#E5B80B_100%)] bg-clip-text text-transparent"
                :class="{'pulse-profit': hasDoubleProfit}"
              >
                <span
                  class="relative mr-1"
                >
                  <CoinIconGold class="w-auto h-4 min-w-[10px]" />
                  <ExplodeEmojisAnimation
                    :show="showCircleLineLottie"
                    :image-src="RocketEmoji"
                    image-rotate-angle="90deg"
                  />
                </span>
                <span
                  class="text-sm font-semibold -tracking-[0.28px]"
                >+{{
                  formatNumberFullTruncate(playerProfit)
                }}</span>
              </span>
              <template #popper>
                <span
                  v-if="hasDoubleProfit"
                  class="flex flex-col items-center gap-y-1"
                >
                  <span class="text-green-primary text-sm font-medium">
                    2x <T
                      key-name="trading_per_hour.boost"
                      default-value="Boost"
                    />
                  </span>
                  <span class="text-white/[.87] text-xs -tracking-[0.24px] flex items-center gap-x-0.5">
                    <ClockIcon class="size-4 min-w-[16px] " />
                    <span class="text-xs font-semibold -tracking-[0.24px]">
                      {{ formattedDoubleProfitCountdown() }}
                    </span>
                  </span>
                </span>
                <span v-else>{{ formatUnshortenedNumber(playerProfit) }}</span>
              </template>
            </VTooltip>
            <InfoCircleIcon
              class="text-white/[.38] w-4 h-4 min-w-[16px]"
              @click="showBoostProfitModal = true"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <BoostProfitModal
    v-if="showBoostProfitModal"
    :show="showBoostProfitModal"
    @hide="showBoostProfitModal = false"
  />
</template>

<style lang="scss">

.profit-tooltip.v-popper--theme-tooltip {

	.v-popper__inner {
		@apply px-2 py-1 font-medium #{!important};
	}
}
</style>

<style lang="scss" scoped>
@keyframes pulse-profit {
	0% {
		transform: scale(1);
		filter: drop-shadow(0px 0px 0px rgba(187, 238, 83, 0)); /* No shadow */
	}
	50% {
		transform: scale(1.03);
		filter: drop-shadow(0px 0px 6px #3B9A7CC6); /* Full shadow */
	}
	100% {
		transform: scale(1); /* Back to original size */
		filter: drop-shadow(0px 0px 6px rgba(187, 238, 83, 0));
	}
}

.pulse-profit {
	animation: pulse-profit 2s infinite;
	will-change: transform, filter;
}

.bg-fill-gradient {
	background: linear-gradient(180deg, #000 54.34%, rgba(0, 0, 0, 0.00) 92.23%);
}

.bg-CNY-gradient {
	background: linear-gradient(165deg, #FF0432 0%, #FF0432 40%, #FFC000 100%);
}
</style>